import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { useAuth } from '../lib/auth/AuthContext';
import { ApiError } from '../lib/api';
import { getAdminSession } from '../lib/api/admin';
import { OverviewTab } from '../components/admin/OverviewTab';
import { UsersTab } from '../components/admin/UsersTab';
import { LeaderboardTab } from '../components/admin/LeaderboardTab';
import { ScheduleTab } from '../components/admin/ScheduleTab';
import { ActivityTab } from '../components/admin/ActivityTab';
import { CatalogTab } from '../components/admin/CatalogTab';
import { EmailSenderTab } from '../components/admin/EmailSenderTab';
import { EmailSendersTab } from '../components/admin/EmailSendersTab';
import { DataUploadTab } from '../components/admin/DataUploadTab';

type GateState = 'checking' | 'authorized' | 'error';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'leaderboard', label: 'Leaderboard' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'email', label: 'Email' },
  { id: 'senders', label: 'Senders' },
  { id: 'activity', label: 'Activity' },
  { id: 'catalog', label: 'Catalog' },
  { id: 'data-upload', label: 'Data Upload' },
] as const;
type TabId = (typeof TABS)[number]['id'];

// Client-side bounce is cosmetic; GET /admin/session is the real check
// (re-reads the DB so a stale token from a demoted admin can't render).
export default function AdminPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gate, setGate] = useState<GateState>('checking');

  const tabParam = searchParams.get('tab');
  const activeTab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : 'overview';

  useEffect(() => {
    applyPageMeta(PAGE_META.admin);
  }, []);

  useEffect(() => {
    if (auth.status === 'loading') return;

    if (!auth.isAuthenticated || auth.user?.type !== 'ADMIN') {
      navigate('/', { replace: true });
      return;
    }

    const controller = new AbortController();
    getAdminSession({ signal: controller.signal })
      .then(() => setGate('authorized'))
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          navigate('/', { replace: true });
          return;
        }
        setGate('error');
      });
    return () => controller.abort();
    // Depend on the specific identity fields the gate reads, not the whole
    // `auth.user` object — a new user reference (e.g. after a token refresh
    // re-issues the same identity) shouldn't re-trigger getAdminSession.
  }, [auth.status, auth.isAuthenticated, auth.user?.type, auth.user?.userId, navigate]);

  if (gate === 'checking') {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-16">
        <p className="text-white/60">Checking access…</p>
      </section>
    );
  }

  if (gate === 'error') {
    return (
      <section className="mx-auto w-full max-w-3xl px-6 py-16">
        <p className="text-white/80">
          Couldn&rsquo;t verify your admin access. Please refresh and try again.
        </p>
      </section>
    );
  }

  const selectTab = (id: TabId): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    next.delete('userId');
    setSearchParams(next, { replace: true });
  };

  const selectedUserId = searchParams.get('userId');
  const setSelectedUserId = (userId: string | null): void => {
    const next = new URLSearchParams(searchParams);
    if (userId) next.set('userId', userId);
    else next.delete('userId');
    setSearchParams(next, { replace: true });
  };

  // Cross-tab jump: switch to the Users tab focused on a specific user (e.g.
  // clicking a player on the Leaderboard tab). Sets both params in one update.
  const openUser = (userId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'users');
    next.set('userId', userId);
    setSearchParams(next, { replace: true });
  };

  return (
    <section className="mx-auto w-full max-w-7xl px-6 py-12">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl uppercase tracking-widest text-brand-redAccent">
          Admin Portal
        </h1>
        <p className="text-sm text-white/60">Signed in as {auth.user?.email ?? 'admin'}.</p>
      </header>

      <div role="tablist" aria-label="Admin sections" className="mt-8 flex flex-wrap gap-2 border-b border-white/10">
        {TABS.map((t) => {
          const selected = t.id === activeTab;
          return (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={selected}
              onClick={() => selectTab(t.id)}
              className={[
                'relative -mb-px rounded-t-md px-4 py-2 font-heading text-sm uppercase tracking-wide transition',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-deep',
                selected
                  ? 'border-b-2 border-brand-redAccent text-white'
                  : 'text-white/55 hover:text-white/85',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && (
          <UsersTab selectedUserId={selectedUserId} onSelect={setSelectedUserId} />
        )}
        {activeTab === 'leaderboard' && <LeaderboardTab onOpenUser={openUser} />}
        {activeTab === 'schedule' && <ScheduleTab />}
        {activeTab === 'email' && <EmailSenderTab />}
        {activeTab === 'senders' && <EmailSendersTab />}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'catalog' && <CatalogTab />}
        {activeTab === 'data-upload' && <DataUploadTab />}
      </div>
    </section>
  );
}
